import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import AuthFrame from './AuthFrame';
import PasswordField from './PasswordField';
import PasswordRequirements from './PasswordRequirements';
import { passwordRequirementChecks } from '../../lib/passwordValidation';
import LoadingSpinner from '../common/LoadingSpinner';

/** Presentation shares the invitation controls; role routing and change API stay intact. */
export default function ForceChangePassword() {
  const { t } = useTranslation('accountAccess');
  const navigate = useNavigate();
  const { user, profile, loading, refreshProfile, signOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const roleHome = () => {
    if (profile?.role === 'admin' || profile?.role === 'super_admin') return '/admin';
    if (profile?.role === 'candidate') return '/candidate';
    if (profile?.role === 'invigilator') return '/invigilator';
    return '/login';
  };
  if (loading) return <AuthFrame><div className="flex justify-center py-8"><LoadingSpinner size="lg" /></div></AuthFrame>;
  if (!user || !profile) return <Navigate to="/login" replace />;
  if (profile.password_changed !== false) return <Navigate to={roleHome()} replace />;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!currentPassword.trim()) { setError(t('missingCurrent')); return; }
    if (!passwordRequirementChecks(newPassword).every(Boolean)) { setError(t('unmet')); return; }
    if (newPassword !== confirmPassword) { setError(t('mismatch')); return; }
    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess(true);
      await refreshProfile();
      redirectTimer.current = setTimeout(() => navigate(roleHome(), { replace: true }), 900);
    } catch (err) { setError(err instanceof Error ? err.message : t('changeFailed')); }
    finally { setSubmitting(false); }
  };

  return <AuthFrame>
    <h1>{t('changeTitle')}</h1><p>{t('changeBody')}</p>
    {success ? <div role="status" className="mt-6 flex items-center gap-2 rounded-xl theme-banner-success p-4 text-sm"><CheckCircle size={18} />{t('updated')}</div> : <form onSubmit={handleSubmit} className="mt-6 space-y-5" aria-busy={submitting}>
      {error && <p role="alert" id="change-password-error" className="theme-banner-danger rounded-xl p-3 text-sm">{error}</p>}
      <PasswordField id="current-password" label={t('current')} value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" />
      <PasswordField id="new-password" label={t('password')} value={newPassword} onChange={setNewPassword} describedBy="change-password-requirements" />
      <PasswordRequirements value={newPassword} id="change-password-requirements" />
      <PasswordField id="confirm-password" label={t('confirm')} value={confirmPassword} onChange={setConfirmPassword} invalid={confirmPassword.length > 0 && newPassword !== confirmPassword} describedBy={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'change-mismatch' : undefined} />
      {confirmPassword.length > 0 && newPassword !== confirmPassword && <p id="change-mismatch" className="text-sm text-[var(--color-error)]">{t('mismatch')}</p>}
      <button type="submit" disabled={submitting} className="btn-primary flex w-full items-center justify-center gap-2 py-3">{submitting && <LoadingSpinner size="sm" />}{t(submitting ? 'submitting' : 'changeSubmit')}</button>
    </form>}
    <button type="button" onClick={() => { void signOut(); }} className="btn-ghost mt-4 w-full">{t('signOut')}</button>
  </AuthFrame>;
}
